/**
 * A fee breakdown presented as gross charges first, discounts grouped after —
 * so a payer sees what was actually charged and what was taken off, rather than
 * a net figure that silently absorbs the discount.
 */

/** A charge at its gross (pre-discount) amount. */
export interface FeeCharge {
    name: string;
    amount: number;
}

/** A discount, held as a positive amount that is subtracted from the subtotal. */
export interface FeeDiscount {
    name: string;
    amount: number;
}

export interface FeeBreakdown {
    charges: FeeCharge[];
    discounts: FeeDiscount[];
    /** Sum of the gross charges. */
    subtotal: number;
    /** subtotal − discounts. */
    total: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

export function buildFeeBreakdown(charges: FeeCharge[], discounts: FeeDiscount[]): FeeBreakdown {
    const subtotal = round2(charges.reduce((sum, c) => sum + c.amount, 0));
    const discountTotal = round2(discounts.reduce((sum, d) => sum + d.amount, 0));
    return {
        charges,
        discounts,
        subtotal,
        total: round2(Math.max(0, subtotal - discountTotal)),
    };
}

/**
 * A persisted invoice line. Covers both `InvoiceDetailItem` (nested `fee_head` /
 * `applied_discount`) and `FeeTransactionInvoiceItem` (flat `fee_head_name`).
 */
export type ApiFeeLine = {
    fee_head?: { name: string } | null;
    fee_head_name?: string | null;
    description?: string | null;
    gross_amount: string;
    discount_amount: string;
    applied_discount?: { name: string } | null;
};

export function feeLineHeadName(it: ApiFeeLine): string {
    return it.fee_head?.name ?? it.fee_head_name ?? 'Fee item';
}

export function feeLineChargeName(it: ApiFeeLine): string {
    const head = feeLineHeadName(it);
    return it.description ? `${head} — ${it.description}` : head;
}

/** Label a discount by its own name where the API supplies one, always tied to the charge it came off. */
export function feeDiscountName(it: ApiFeeLine): string {
    const head = feeLineHeadName(it);
    return it.applied_discount?.name ? `${it.applied_discount.name} — ${head}` : `Discount — ${head}`;
}

/** Split persisted invoice lines into gross charges and a grouped discount list. */
export function breakdownFromApiItems(items: ApiFeeLine[]): FeeBreakdown {
    const charges: FeeCharge[] = [];
    const discounts: FeeDiscount[] = [];

    for (const it of items) {
        charges.push({ name: feeLineChargeName(it), amount: parseFloat(it.gross_amount) || 0 });
        const discount = parseFloat(it.discount_amount) || 0;
        if (discount > 0) discounts.push({ name: feeDiscountName(it), amount: discount });
    }

    return buildFeeBreakdown(charges, discounts);
}

/** Single-line stand-in for an invoice whose lines the API did not expand. */
export function fallbackBreakdown(name: string, amount: number): FeeBreakdown {
    return buildFeeBreakdown([{ name, amount }], []);
}
